<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    min-width="auto">
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-model="displayedValue"
        :filled="filled"
        :label="label"
        :prepend-inner-icon="prependInnerIcon"
        readonly v-bind="attrs"
        :error="error"
        :error-messages="errorMessages"
        v-on="on"/>
    </template>
    <v-date-picker v-model="internalValue" :type="type" no-title scrollable @input="menu = false" :max="max" :allowed-dates="allowedDates">
      <v-btn v-if="today" text color="primary" @click="setToToday()">
        {{ $t('today') }}
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn text small fab color="primary" @click="clear()">
        <v-icon>mdi-backspace</v-icon>
      </v-btn>
      <v-btn text small color="primary" fab @click="menu = false">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-date-picker>
  </v-menu>
</template>

<script>
import { isEmpty } from 'lodash'
import Moment from 'moment-timezone'

/**
 * Wrapper for v-datepicker for use in Verofresh app.
 * Accepts Moment objects, Strings, and JS Dates. Outputs either ISO-8601 strings, or Moment objects.
 * Contains a typical setup with our defaults so we don't have to continually re-declare them, and allows passing
 * in a timezone to ease our use of customer timezones.
 *
 * TODO: this implementation currently suffers from a bug in Vuetify where if the text field has a label, the initial
 * click may not register on the text field correctly and fail to trigger the activation slot on the v-menu. I have
 * verified this is a bug in Vuetify as per this example: https://vuetifyjs.com/en/components/date-pickers/#active-picker
 * in October 2022.
 */

export default {
  name: 'DatePicker',
  props: {
    value: {
      type: [Object, String]
    },
    label: {
      type: String
    },
    /**
     * Passthrough for the datepicker 'type' parameter
     */
    type: {
      type: String,
      default: 'date'
    },
    /**
     * Whether to set the selected date to the end of the day. Useful for the end point of a date range.
     */
    endOfDay: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to reset the date when the timezone changes. This is useful for index forms where the date picker
     * is used for filtering, but it's not desirable when the date picker is used on an entity/model form.
     */
    resetOnTimezoneChange: {
      type: Boolean,
      default: false
    },
    /**
     * Whether the date should be output as a Moment object.
     * If false, the date will be output as an ISO-8601 string.
     *
     * In some cases it's nice to have the Moment output, but in other cases (such as dealing with entities) it can
     * be a bit confusing to juggle strings and Moment objects.
     */
    outputMoment: {
      type: Boolean,
      default: false
    },
    /**
     * The current timezone. Defaults to UTC if none provided.
     */
    timezone: {
      type: String,
      default: 'UTC'
    },
    /**
     * Pass-through property to 'prependInnerIcon' on the read-only text field.
     */
    prependInnerIcon: {
      type: String
    },
    /**
     * Pass-through property to 'filled' on the read-only text field.
     */
    filled: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to show the 'Today' button
     */
    today: {
      type: Boolean,
      default: true
    },
    /**
     * Pass-through property for the 'error' property (delegates to the text box)
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Pass-through property for the 'error-messages' property (delegates to the text box)
     */
    errorMessages: {
      default: () => []
    },
    /**
     * Pass-through property for the 'max' property
     */
    max: {
      default: null
    },
    /**
     * Pass-through for "allowedDates" property
     */
    allowedDates: {
      default: null
    }
  },
  data () {
    return {
      // The display flag for the v-menu component.
      menu: false
    }
  },
  computed: {
    displayedValue () {
      if (this.internalValue) {
        return Moment(this.internalValue).format('L')
      }
      return undefined
    },
    /**
     * A computed getter/setter to assist with interfacing Moment objects to the internal date picker component.
     */
    internalValue: {
      get: function () {
        if (isEmpty(this.value)) {
          return null
        }
        // Convert to the appropriate timezone then back out to ISO-8601 string
        return Moment.tz(this.value, this.timezone).format('YYYY-MM-DD')
      },
      set: function (newValue) {
        if (isEmpty(newValue)) {
          this.$emit('input', null)
          return
        }
        // Convert to appropriate timezone
        const result = Moment.tz(newValue, this.timezone)
        // If requested, set the value to the end of the day
        if (this.endOfDay) {
          result.endOf('day')
        }
        // Return as moment if required, otherwise output ISO-8601 string
        if (this.outputMoment) {
          this.$emit('input', result)
        } else {
          this.$emit('input', result.format())
        }
      }
    }
  },
  methods: {
    /**
     * Clear the date.
     */
    clear () {
      this.internalValue = null
      this.menu = false
    },
    /**
     * Set the date to today's date.
     */
    setToToday () {
      this.internalValue = Moment().startOf('day')
      this.menu = false
    }
  },
  watch: {
    /**
     * Watch for timezone changes and re-emit the value. If resetOnTimezoneChange is enabled, reset
     * to either the start or end of the day, depending on the endOfDay property.
     * @param newZone
     * @param oldZone
     */
    timezone (newZone, oldZone) {
      if (this.value != null) {
        let newValue
        if (this.resetOnTimezoneChange) {
          // Preserve the date by formatting as a string then creating a new Moment with from it the new timezone.
          const oldDate = Moment.tz(this.value, oldZone).format('YYYY-MM-DD')
          newValue = Moment.tz(oldDate, newZone)
          if (this.endOfDay) {
            newValue.endOf('day')
          }
        } else {
          newValue = Moment.tz(this.value, newZone)
        }
        if (this.outputMoment) {
          this.$emit('input', newValue)
        } else {
          this.$emit('input', newValue.format())
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
